/**
 * User Service - API calls for user-related operations
 * Handles authentication, user management, and profile operations with RBAC support
 */

import { apiClient } from "./apiService";

const api = apiClient;

class UserService {
  // Authentication
  async login(credentials) {
    const response = await api.post("/users/login", credentials);
    return response.data;
  }

  async register(userData) {
    const response = await api.post("/users/register", userData);
    return response.data;
  }

  async logout() {
    // Clear local storage
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  }

  async forgotPassword(email) {
    const response = await api.post("/users/forgot-password", { email });
    return response.data;
  }

  async resetPassword(resetData) {
    const response = await api.post("/users/reset-password", resetData);
    return response.data;
  }

  async confirmResetPassword(token, newPassword) {
    const response = await api.post("/users/reset-password/confirm", { token, newPassword });
    return response.data;
  }

  // User Profile
  async getCurrentUser() {
    const response = await api.get("/users/profile");
    return response.data;
  }

  async updateProfile(profileData) {
    const response = await api.put("/users/profile", profileData);
    return response.data;
  }

  async updateAvatar(avatarUrl, avatarType = 'generic') {
    // Dedicated endpoint: changes only avatar_url / avatar_type.
    const response = await api.put("/users/avatar", {
      avatar_url: avatarUrl,
      avatar_type: avatarType,
    });
    return response.data;
  }

  async getSubscriptionPlans() {
    const response = await api.get("/users/plans");
    return response.data;
  }

  async requestRoleUpgrade(planId, options = {}) {
    const response = await api.post("/users/upgrade", {
      planId,
      ...options,
    });
    return response.data;
  }

  // Username operations
  async checkUsernameAvailability(username) {
    const response = await api.get(`/users/username/check/${username}`);
    return response.data;
  }

  async checkEmailAvailability(email) {
    const response = await api.get(`/users/email/check/${encodeURIComponent(email)}`);
    return response.data;
  }

  async searchByUsername(searchTerm) {
    const response = await api.get('/users/username/search', { params: { q: searchTerm } });
    return response.data;
  }

  async getUserByUsername(username) {
    const response = await api.get(`/users/username/${username}`);
    return response.data;
  }

  // RBAC User Management (Super Admin functions)
  async getAllUsers(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== "") {
        queryParams.append(key, params[key]);
      }
    });

    const response = await api.get(`/users?${queryParams.toString()}`);
    return response.data;
  }

  async getUserById(userId) {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  }

  async getPublicProfile(userId) {
    const response = await api.get(`/users/${userId}/public`);
    return response.data;
  }

  async createUser(userData) {
    const response = await api.post("/users", userData);
    return response.data;
  }

  async updateUser(userId, userData) {
    const response = await api.put(`/users/${userId}`, userData);
    return response.data;
  }

  async deleteUser(userId) {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  }

  async updateUserPassword(userId, passwordData) {
    const response = await api.put(`/users/${userId}/password`, passwordData);
    return response.data;
  }

  // Role & Permission Management
  async getAllRoles() {
    const response = await api.get("/users/roles/all");
    return response.data;
  }

  async getRoleById(roleId) {
    const response = await api.get(`/users/roles/${roleId}`);
    return response.data;
  }

  async getAllPermissions() {
    const response = await api.get("/users/permissions/all");
    return response.data;
  }

  async getUserPermissions(userId) {
    const response = await api.get(`/users/${userId}/permissions`);
    return response.data;
  }

  async checkUserPermission(userId, permission) {
    const response = await api.get(`/users/${userId}/check-permission`, {
      params: { permission },
    });
    return response.data;
  }

  // Statistics
  async getUserStats() {
    const response = await api.get("/users/stats");
    return response.data;
  }

  async getRoleStats() {
    const response = await api.get("/users/roles/stats");
    return response.data;
  }

  // Legacy methods for backward compatibility
  async getUsers(page = 1, limit = 10, filters = {}) {
    return await this.getAllUsers({ page, limit, ...filters });
  }

  async getPermissions() {
    return await this.getAllPermissions();
  }

  async getRoles() {
    return await this.getAllRoles();
  }

  async changeUserPassword(userId, passwordData) {
    return await this.updateUserPassword(userId, passwordData);
  }

  async checkPermission(userId, permission) {
    return await this.checkUserPermission(userId, permission);
  }
}

const userService = new UserService();

// Create authService alias for authentication-specific operations
export const authService = {
  login: (credentials) => userService.login(credentials),
  register: (userData) => userService.register(userData),
  logout: () => userService.logout(),
  getCurrentUser: () => userService.getCurrentUser(),
  updateProfile: (profileData) => userService.updateProfile(profileData),
  resetPassword: (resetData) => userService.resetPassword(resetData),
  forgotPassword: (email) => userService.forgotPassword(email),
};

// Export both class instance and individual functions for backward compatibility
export { userService };

// Legacy individual function exports
export const registerUser = (userData) => userService.register(userData);
export const loginUser = (userData) => userService.login(userData);
export const getUserProfile = () => userService.getCurrentUser();
export const resetPassword = (resetData) =>
  userService.resetPassword(resetData);
export const confirmResetPassword = (token, newPassword) =>
  userService.confirmResetPassword(token, newPassword);
export const updateUserProfile = (profileData) =>
  userService.updateProfile(profileData);
export const updateUserAvatar = (avatarUrl, avatarType = 'generic') =>
  userService.updateAvatar(avatarUrl, avatarType);
export const getExtendedProfile = () => userService.getCurrentUser();
export const getSubscriptionPlans = () => userService.getSubscriptionPlans();
export const requestRoleUpgrade = (planId, options = {}) =>
  userService.requestRoleUpgrade(planId, options);

// Username operations
export const checkUsernameAvailability = (username) =>
  userService.checkUsernameAvailability(username);
export const checkEmailAvailability = (email) =>
  userService.checkEmailAvailability(email);
export const searchByUsername = (searchTerm) =>
  userService.searchByUsername(searchTerm);
export const getUserByUsername = (username) =>
  userService.getUserByUsername(username);
export const getPublicProfile = (userId) =>
  userService.getPublicProfile(userId);

// Block / mute another player. Each resolves to the API body ({ success, data }).
export const blockUser = (userId) =>
  apiClient.post(`/users/${userId}/block`).then((r) => r.data);
export const unblockUser = (userId) =>
  apiClient.delete(`/users/${userId}/block`).then((r) => r.data);
export const muteUser = (userId) =>
  apiClient.post(`/users/${userId}/mute`).then((r) => r.data);
export const unmuteUser = (userId) =>
  apiClient.delete(`/users/${userId}/mute`).then((r) => r.data);
// { blocked: [...], muted: [...] } with public fields only
export const getMyBlocks = () =>
  apiClient.get("/users/me/blocks").then((r) => r.data);

// Self-service account management
export const forgotPassword = (data) =>
  apiClient.post("/users/forgot-password", data);
export const changePassword = (currentPassword, newPassword, confirmPassword) =>
  apiClient.post("/users/account/change-password", { currentPassword, newPassword, confirmPassword });
export const deactivateAccount = (reason) =>
  apiClient.post("/users/account/deactivate", { reason });
export const deleteAccount = (password, reason) =>
  apiClient.post("/users/account/delete", { password, reason });
// Emails a one-time reactivation link (PR-B08); always 200.
export const requestReactivation = (data) =>
  apiClient.post("/users/account/reactivate", data);
// Redeems the emailed link with a new password.
export const confirmReactivation = (token, newPassword) =>
  apiClient.post("/users/account/reactivate/confirm", { token, newPassword });

// Claim your profile: a host added this person (placeholder) and emailed a link.
// Who the link is for: { player, email, teams, expiresAt, accountExists }.
export const getClaim = (token) =>
  apiClient.get(`/claims/${encodeURIComponent(token)}`).then((r) => r.data);
// Signed in: merge the placeholder into this account.
export const acceptClaim = (token) =>
  apiClient.post(`/claims/${encodeURIComponent(token)}/accept`).then((r) => r.data);
// New account with the invite email, then claim: { name, password }.
export const registerAndClaim = (token, data) =>
  apiClient.post(`/claims/${encodeURIComponent(token)}/register`, data).then((r) => r.data);

// Host side (league host, team owner / manager / captain, admins)
export const listPlaceholderPlayers = (teamId) =>
  apiClient.get(`/teams/${teamId}/placeholders`).then((r) => r.data);
export const addPlaceholderPlayer = (teamId, data) =>
  apiClient.post(`/teams/${teamId}/placeholders`, data).then((r) => r.data);
export const setPlaceholderContact = (teamId, userId, data) =>
  apiClient.put(`/teams/${teamId}/members/${userId}/contact`, data).then((r) => r.data);
export const sendClaimInvite = (teamId, userId) =>
  apiClient.post(`/teams/${teamId}/members/${userId}/invite`).then((r) => r.data);
